import { useMemo } from "react";

import type { ProductAttribute, StoreDetailsDataProps, StoreDetailsProductType } from "../../types";
import { getAttributeWithValues, getAvailableItems } from "../../utils/detailPageHelpers";

interface StoreVisibilityFlags {
  isOpeningHoursVisible: boolean;
  isStoreTypeVisible: boolean;
  isGeneralAnnouncementVisible: boolean;
  isAvailableServicesVisible: boolean;
  isAvailableProductsVisible: boolean;
  isAvailableSticksVisible: boolean;
  isMultiBrandSticksVisible: boolean;
  isVendingMachineVisible: boolean;
  isNearbyShopsEnabled: boolean;
}

interface ProcessedStoreDataProductAttributes {
  productTypes: ProductAttribute | null;
  productFlavours: ProductAttribute | null;
  productFlavoursLYO: ProductAttribute | null;
  productFlavoursVendingMachine: ProductAttribute | null;
  productFlavoursLYOVendingMachine: ProductAttribute | null;
}

interface ProcessedStoreData {
  availableProducts: ProductAttribute[];
  availableProductsVendingMachine: ProductAttribute[];
  availableServices: StoreDetailsProductType[];
  productAttributes: ProcessedStoreDataProductAttributes;
}

interface UseStoreVisibilityReturn {
  visibility: StoreVisibilityFlags;
  processedData: ProcessedStoreData;
}

const TOBACCONIST_AND_VENDING_MACHINE = "Tobacconist with Vending Machine";

export const useStoreVisibility = (storeDetails: StoreDetailsDataProps): UseStoreVisibilityReturn => {
  return useMemo(() => {
    const store = storeDetails?.store;

    const productAttributes = {
      productTypes: getAttributeWithValues(store?.product_attributes, "ProductTypes"),
      productFlavours: getAttributeWithValues(store?.product_attributes, "ProductFlavours"),
      productFlavoursLYO: getAttributeWithValues(store?.product_attributes, "ProductFlavoursLYO"),
      productFlavoursVendingMachine: getAttributeWithValues(
        store?.product_attributes_vending_machine,
        "ProductFlavoursVendingMachine"
      ),
      productFlavoursLYOVendingMachine: getAttributeWithValues(
        store?.product_attributes_vending_machine,
        "ProductFlavoursLYOVendingMachine"
      ),
    };

    const availableProducts = getAvailableItems(store?.product_types, "is_available");
    const availableProductsVendingMachine = getAvailableItems(store?.product_types_vending_machine, "is_available");
    const availableServices = getAvailableItems(store?.services, "is_available");

    const isVendingMachineAvailable =
      storeDetails?.isVendingMachineEnabled && store?.storetype === TOBACCONIST_AND_VENDING_MACHINE;

    const visibility: StoreVisibilityFlags = {
      isNearbyShopsEnabled: Boolean(storeDetails?.isNearbyShopsEnabled),
      isOpeningHoursVisible: Boolean(storeDetails?.isOpeningHoursEnabled && !!store?.opening_hours?.length),

      isStoreTypeVisible: Boolean(storeDetails?.isStoreTypeEnabled && !!store?.storetype),

      isGeneralAnnouncementVisible: Boolean(
        storeDetails?.isGeneralAnnouncementEnabled && !!store?.opening_hours?.[0]?.general_announcement?.length
      ),

      isAvailableServicesVisible: Boolean(storeDetails?.isAvailableServicesEnabled && availableServices.length > 0),

      isAvailableProductsVisible: Boolean(storeDetails?.isAvailableDevicesEnabled && availableProducts.length > 0),

      isAvailableSticksVisible: Boolean(
        storeDetails?.isAvailableSticksEnabled && !!productAttributes.productFlavours?.values?.length
      ),

      isMultiBrandSticksVisible: Boolean(storeDetails?.isMultiBrandSticksEnabled),

      isVendingMachineVisible:
        Boolean(isVendingMachineAvailable) &&
        (!!productAttributes.productFlavoursVendingMachine?.values?.length ||
          availableProductsVendingMachine.length > 0),
    };

    return {
      visibility,
      processedData: {
        availableProducts,
        availableProductsVendingMachine,
        productAttributes,
        availableServices,
      },
    };
  }, [storeDetails]);
};
